/**
 * Issue Detail Dialog Component
 * Read-only dialog showing full issue details for a test case.
 */

import { CheckCircle2, ExternalLink } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { IssueSnapshot } from '@/types/domain';

const SEVERITY_LABELS: Record<string, string> = {
  '1_Critical': 'Critical',
  '2_High': 'High',
  '3_Medium': 'Medium',
  '4_Low': 'Low',
};

interface IssueDetailDialogProps {
  issue: IssueSnapshot;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function IssueDetailDialog({
  issue,
  open,
  onOpenChange,
}: IssueDetailDialogProps) {
  const severityLabel = SEVERITY_LABELS[issue.severity] ?? issue.severity;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Issue Details</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Title + Severity */}
          <div className="flex items-start justify-between gap-3">
            <p className="text-sm font-medium">{issue.title}</p>
            <Badge
              className={cn(
                'text-xs shrink-0',
                issue.severity === '1_Critical' && 'bg-red-100 text-red-700 border-red-200',
                issue.severity === '2_High' && 'bg-orange-100 text-orange-700 border-orange-200',
                issue.severity === '3_Medium' && 'bg-yellow-100 text-yellow-700 border-yellow-200',
                issue.severity === '4_Low' && 'bg-blue-100 text-blue-700 border-blue-200',
              )}
            >
              {severityLabel}
            </Badge>
          </div>

          {/* Description */}
          {issue.description && (
            <div className="rounded-lg bg-muted p-3">
              <p className="text-xs font-medium text-muted-foreground mb-1">Description</p>
              <p className="text-sm whitespace-pre-wrap">{issue.description}</p>
            </div>
          )}

          {/* Metadata */}
          <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
            <div>
              <p className="text-xs text-muted-foreground">Status</p>
              <p className="font-medium capitalize">{issue.status}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Created</p>
              <p className="font-medium">
                {new Date(issue.created_at).toLocaleDateString('en-GB', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </p>
            </div>
          </div>

          {/* SharePoint sync status */}
          <div className="flex items-center gap-2">
            {issue.sharepoint_synced ? (
              <Badge className="bg-green-100 text-green-700 border-green-200">
                <CheckCircle2 className="mr-1 h-3 w-3" /> Synced to SharePoint
              </Badge>
            ) : (
              <Badge className="bg-yellow-100 text-yellow-700 border-yellow-200">
                Pending SharePoint sync
              </Badge>
            )}
          </div>

          {/* SharePoint link */}
          {issue.sharepoint_web_url && (
            <a
              href={issue.sharepoint_web_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline"
            >
              <ExternalLink className="h-3.5 w-3.5" />
              View on SharePoint Action List
            </a>
          )}
        </div>

        <DialogFooter>
          <Button onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
